import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  listAdminEvents, deleteAdminEvent, restoreAdminEvent,
  patchAdminEvent, listSpeakers,
} from '../lib/api.js'
import ErrorAlert from '../components/ErrorAlert.jsx'
import { PAGE_STANDARD } from '../lib/layout.js'
import { conferenceLabel } from '../lib/text.js'
import { BTN_PRIMARY, INPUT } from '../lib/ui.js'

const STATUSES = ['DRAFT', 'TENTATIVE', 'PUBLISHED', 'CANCELLED']
const STATUS_BADGE = {
  DRAFT: 'bg-amber-100 text-amber-800',
  TENTATIVE: 'bg-sky-100 text-sky-800',
  PUBLISHED: 'bg-emerald-100 text-emerald-800',
  CANCELLED: 'bg-rose-100 text-rose-800',
}

function formatDate(iso) {
  if (!iso) return ''
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  }).format(new Date(iso))
}


export default function AdminEventsPage() {
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [statusFilter, setStatusFilter] = useState('')
  const [speakerFilter, setSpeakerFilter] = useState('')
  const [q, setQ] = useState('')
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [speakers, setSpeakers] = useState([])

  useEffect(() => { listSpeakers().then(setSpeakers).catch(() => setSpeakers([])) }, [])

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    listAdminEvents({
      status: statusFilter || undefined,
      speaker_id: speakerFilter || undefined,
      q: q.trim() || undefined,
      includeDeleted,
      size: 200,
    })
      .then((page) => {
        setRows(page.content || [])
        setTotal(page.totalElements ?? (page.content?.length || 0))
      })
      .catch((err) => setError(err.message || String(err)))
      .finally(() => setLoading(false))
  }, [statusFilter, speakerFilter, q, includeDeleted])

  useEffect(() => { load() }, [load])

  const updateStatus = async (id, status) => {
    try {
      const updated = await patchAdminEvent(id, { status })
      setRows((prev) => prev.map((r) => (r.id === id ? updated : r)))
    } catch (err) {
      setError(err.message || String(err))
    }
  }

  const remove = async (id, title) => {
    if (!window.confirm(`Soft-delete "${title}"? Visible only with "Show deleted" on.`)) return
    try {
      await deleteAdminEvent(id)
      load()
    } catch (err) {
      setError(err.message || String(err))
    }
  }

  const restore = async (id) => {
    try {
      const restored = await restoreAdminEvent(id)
      setRows((prev) => prev.map((r) => (r.id === id ? restored : r)))
    } catch (err) {
      setError(err.message || String(err))
    }
  }

  return (
    <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            <span className="brand-gradient-text">All events</span>
          </h1>
          <p className="mt-2 text-base text-slate-600">
            Every event in the system, including drafts and (optionally) soft-deleted rows.
          </p>
        </div>
        <Link
          to="/admin/events/new"
          className={BTN_PRIMARY}
        >
          New event
        </Link>
      </header>

      <section className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/60">
        <label className="text-sm">
          <span className="block text-xs font-medium text-slate-600">Status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`mt-1 ${INPUT}`}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs font-medium text-slate-600">Speaker</span>
          <select value={speakerFilter} onChange={(e) => setSpeakerFilter(e.target.value)} className={`mt-1 ${INPUT}`}>
            <option value="">All speakers</option>
            {speakers.map((s) => (
              <option key={s.userId} value={s.userId}>
                {s.displayName || s.email || `User #${s.userId}`}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm flex-1 min-w-[180px]">
          <span className="block text-xs font-medium text-slate-600">Search title</span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className={`mt-1 w-full ${INPUT}`}
            placeholder="contains…"
          />
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          />
          <span className="text-slate-700">Show deleted</span>
        </label>
      </section>

      {error ? <ErrorAlert title="Couldn't load events" message={error} source="AdminEventsPage" /> : null}

      <div className="text-xs text-slate-500 mb-2">{total} event{total === 1 ? '' : 's'}</div>

      {loading ? (
        <div className="rounded-2xl bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200/60">
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200/60">
          <h3 className="text-base font-semibold text-slate-900">No events match those filters</h3>
        </div>
      ) : (
        /* #356: `overflow-hidden` CLIPPED the table at narrow widths, so the
           right-hand columns — which is where the actions live — were simply
           unreachable on a phone. `overflow-x-auto` plus an explicit min-width
           makes the table scroll inside its own card instead, which also keeps
           the page itself from scrolling sideways. */
        <div className="overflow-x-auto rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/60">
          <table className="min-w-[760px] w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2">Title</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Conference</th>
                <th className="px-4 py-2">Main speaker</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((e) => {
                const main = (e.speakers || []).find((s) => s.isMain)
                const isDeleted = !!e.deletedAt // server doesn't send this in EventDto today, but reserved for the flag
                return (
                  <tr key={e.id} className={isDeleted ? 'opacity-60' : ''}>
                    <td className="px-4 py-3 align-top">
                      <Link to={`/admin/events/${e.id}`} className="font-medium text-slate-900 hover:text-brand-700">
                        {e.title}
                      </Link>
                      {e.format ? (
                        <div className="mt-0.5 text-xs text-slate-500">{e.format.toLowerCase().replace('_', '-')}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 align-top text-slate-600">{formatDate(e.startsAtUtc)}</td>
                    {/* #341: conferenceLabel, not the raw field — it is a nested
                        object since #307 and React refuses to render one. */}
                    <td className="px-4 py-3 align-top text-slate-600">{conferenceLabel(e) || '—'}</td>
                    <td className="px-4 py-3 align-top text-slate-600">
                      {main?.displayName || (main ? `User #${main.userId}` : '—')}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <select
                        value={e.status}
                        onChange={(ev) => updateStatus(e.id, ev.target.value)}
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[e.status] || 'bg-slate-100 text-slate-700'}`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      <Link
                        to={`/admin/events/${e.id}`}
                        className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
                      >
                        Edit
                      </Link>
                      {includeDeleted ? (
                        <button
                          type="button"
                          onClick={() => restore(e.id)}
                          className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                        >
                          Restore
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => remove(e.id, e.title)}
                          className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}
